import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * Reads --drawer-animation-speed from the given element and converts it to
 * milliseconds. Falls back to a safe default if the variable is missing or
 * unparsable, so this never breaks even if the CSS variable's definition
 * changes elsewhere in the theme.
 * @param {Element} element
 * @returns {number}
 */
function getDrawerAnimationSpeedMs(element) {
  const FALLBACK_MS = 400;
  const raw = getComputedStyle(element).getPropertyValue('--drawer-animation-speed').trim();

  if (!raw) return FALLBACK_MS;
  if (raw.endsWith('ms')) {
    const value = parseFloat(raw);
    return isNaN(value) ? FALLBACK_MS : value;
  }
  if (raw.endsWith('s')) {
    const value = parseFloat(raw);
    return isNaN(value) ? FALLBACK_MS : value * 1000;
  }

  const value = parseFloat(raw);
  return isNaN(value) ? FALLBACK_MS : value;
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    // Checks the `menu-open` class (always updated synchronously by our own
    // code) rather than the native `open` attribute, so there's never a
    // window where this reports a stale value.
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    // Force a reflow before toggling classes, so the browser has settled
    // any previous state change before we apply a new one.
    void details.offsetHeight;

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      details.setAttribute('open', '');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait a fixed, deterministic amount of time (matching the CSS
      // transition duration) before trapping focus, instead of relying on
      // getAnimations()/animationend, which has shown intermittent,
      // hard-to-reproduce timing issues on iOS Safari with rapid repeated
      // open/close cycles.
      const speed = getDrawerAnimationSpeedMs(details);
      setTimeout(() => trapFocus(details), speed);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer (animated)
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the main menu drawer immediately, without waiting for the closing
   * animation/transition to finish.
   *
   * Used by actual navigation links inside the drawer (as opposed to the
   * close button / backdrop), since the page is about to change anyway and
   * there's no point animating a drawer the user is navigating away from.
   */
  closeImmediate() {
    const details = this.refs.details;

    // Remove the visual state synchronously so isOpen() is accurate the
    // instant this returns.
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');

    details.removeAttribute('open');
    removeTrapFocus();

    const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
    openDetails.forEach(reset);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');

    requestAnimationFrame(() => {
      details.classList.remove('menu-open');
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

      // Wait a fixed, deterministic amount of time (matching the CSS
      // transition duration) before actually resetting the details
      // element, instead of relying on getAnimations()/animationend.
      const speed = getDrawerAnimationSpeedMs(details);

      setTimeout(() => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      }, speed);
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}